var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, MapSchema, type } from "@colyseus/schema";
import { Appearance } from "./appearance.js";
/**
 * Authoritative blackjack table state, synced to every client as sub-state on
 * `GameState` (mirroring `groundItems`). The whole table lives in ONE shared world
 * room — there is no separate Colyseus room.
 *
 * Design notes:
 * - All PLAYER cards are public (face-up, as in real casino blackjack). Only the
 *   dealer's hole card is hidden — it lives in a server-only field and is appended
 *   to `Dealer.cards` at reveal, so nothing private travels in this schema.
 * - Card lists are a compact space-separated string (e.g. "AS TD 9C"), and a seat
 *   holds at most two explicit `Hand` fields. This uses only decorator patterns
 *   already proven in the repo (primitive / nested-Schema / MapSchema) — there is
 *   no ArraySchema here by design.
 *
 * NOTE: like `state.ts`, compiled with `tsc` (legacy decorators); must NOT be
 * transformed by esbuild/Vite — consumers import the built `dist/` output.
 */
/** One blackjack hand. A seat has a primary `hand` and, after a split, a `splitHand`. */
export class Hand extends Schema {
    constructor() {
        super(...arguments);
        /** Face-up cards as space-separated codes, e.g. "AS TD". Empty before dealing. */
        this.cards = "";
        /** Best total (ace-soft aware). */
        this.value = 0;
        /** Whether `value` counts an ace as 11 (a "soft" total). */
        this.soft = false;
        /** empty | playing | stand | bust | blackjack | won | lost | push */
        this.status = "empty";
        /** Wager held on this hand (public — bets are visible at the table). */
        this.bet = 0;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Hand.prototype, "cards", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Hand.prototype, "value", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Hand.prototype, "soft", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Hand.prototype, "status", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Hand.prototype, "bet", void 0);
/** One of the seven chairs. Pre-seeded for every index; `occupied` toggles use. */
export class Seat extends Schema {
    constructor() {
        super(...arguments);
        this.occupied = false;
        /** Occupant display name + sprite (drives the seat portrait). */
        this.name = "";
        this.spriteId = 0;
        /** Occupant's layered appearance (replaces spriteId; copied from the Player on sit). */
        this.appearance = new Appearance();
        /** False while the occupant is disconnected mid-round (their hand still rides). */
        this.connected = true;
        /** True when seated mid-round; becomes eligible from the next betting window. */
        this.spectating = false;
        /** Chosen bet during the betting window (0 = not yet bet). */
        this.pendingBet = 0;
        this.hand = new Hand();
        this.splitHand = new Hand();
        this.hasSplit = false;
    }
}
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Seat.prototype, "occupied", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Seat.prototype, "name", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Seat.prototype, "spriteId", void 0);
__decorate([
    type(Appearance),
    __metadata("design:type", Object)
], Seat.prototype, "appearance", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Seat.prototype, "connected", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Seat.prototype, "spectating", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Seat.prototype, "pendingBet", void 0);
__decorate([
    type(Hand),
    __metadata("design:type", Object)
], Seat.prototype, "hand", void 0);
__decorate([
    type(Hand),
    __metadata("design:type", Object)
], Seat.prototype, "splitHand", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Seat.prototype, "hasSplit", void 0);
/** The house dealer. `cards` holds only revealed cards; the hole is server-only. */
export class Dealer extends Schema {
    constructor() {
        super(...arguments);
        this.cards = "";
        this.value = 0;
        this.soft = false;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Dealer.prototype, "cards", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Dealer.prototype, "value", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Dealer.prototype, "soft", void 0);
export class BlackjackTable extends Schema {
    constructor() {
        super(...arguments);
        /** idle | betting | dealing | turns | dealer | settle | payout | reset */
        this.phase = "idle";
        /** Seat index (0..6) whose turn it is, or -1 when no seat is acting. */
        this.activeSeat = -1;
        /** Whether the active seat is currently playing its split hand. */
        this.activeSplitHand = false;
        /** Server-clock ms at which the current phase timer expires (client countdown). */
        this.timerEndsAt = 0;
        this.dealer = new Dealer();
        /** The seven chairs, keyed "0".."6". */
        this.seats = new MapSchema();
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], BlackjackTable.prototype, "phase", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], BlackjackTable.prototype, "activeSeat", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], BlackjackTable.prototype, "activeSplitHand", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], BlackjackTable.prototype, "timerEndsAt", void 0);
__decorate([
    type(Dealer),
    __metadata("design:type", Object)
], BlackjackTable.prototype, "dealer", void 0);
__decorate([
    type({ map: Seat }),
    __metadata("design:type", Object)
], BlackjackTable.prototype, "seats", void 0);
//# sourceMappingURL=blackjack.js.map